import React, { useEffect, useState } from 'react';

// Hooks
import { useSelector, useDispatch } from 'react-redux';
import useBroadcastChannel from '~/hooks/broadcastChannel';

// Actions
import { updateMode } from '~/redux/actions/mode-actions';
import { getUserData } from '~/redux/actions/auth-actions';
import { getAllPaymentMethods } from '~/redux/actions/stripe-actions';
import { CLEAR_CONTACTS_FILTERS } from '~/redux/actions/action-types';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MODE_SWITCHING_URL } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';

// Components
import Stripe from '~/components/Stripe/Stripe';
import PaymentMethod from '~/components/Account/Wallet/PaymentMethod';
import ConfirmModal from '../General/ConfirmModal';

// MUI Components
import { Box, CircularProgress, Typography } from '@mui/material';

// Images
import { ModeModalCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const Mode = ({ tooltipPlacement = 'right' }) => {
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentMethodMonitor, setPaymentMethodMonitor] = useState(false);
  const [modeChanging, setModeChanging] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  const dispatch = useDispatch();
  const { broadcastMessage } = useBroadcastChannel('mode-update');

  const currentPath = window?.location?.pathname;
  const currentMode = useSelector((state) =>
    state.userReducers.user.isLiveMode === true
      ? 'Live'
      : state.userReducers.user.isLiveMode === false
        ? 'Test'
        : null
  );
  const paymentMethods = useSelector((state) => state.stripeReducers.methods);

  const handleOpenConfirmModal = () => {
    setOpenConfirmModal({
      type: 'testMode',
      open: true,
      message: MESSAGES.MODE.MODAL.TEST_MODE_MESSAGE,
      description: MESSAGES.MODE.MODAL.TEST_MODE_DESCRIPTION,
      handleOk: () => {
        changeMode('Test');
      },
      successButtonName: 'Yes',
      loading: false,
      header: 'Test Mode',
      icon: ModeModalCIcon,
      className: 'modeModal',
      modalName: '',
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
    });
  };

  const handleGetAllMethods = () => {
    dispatch(getAllPaymentMethods());
  };

  const handleUpdate = (message) => {
    if (message.type === 'UPDATE_MODE') {
      dispatch(getUserData());
    }
  };

  const changeMode = async (mode) => {
    if (currentMode === mode) return;
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    if (mode == 'Live' && paymentMethods.length == 0) {
      setPaymentModal(true);
      setPaymentMethodMonitor(true);
      return;
    }
    const payload = {
      mode: mode?.toLowerCase(),
    };
    setModeChanging(true);
    const request = await dispatch(updateMode(payload));
    if (request.status === 200) {
      dispatch(getUserData());
      setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      dispatch({ type: CLEAR_CONTACTS_FILTERS });
      broadcastMessage({ type: 'UPDATE_MODE', data: mode });
    } else if (request?.response?.status === 422) {
      dispatch(failure(request?.response?.data?.message));
    } else {
      dispatch(failure(MESSAGES.GENERAL_ERROR));
    }
    setModeChanging(false);
    setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
  };

  const handleToggle = () => {
    const newMode = currentMode === 'Live' ? 'Test' : 'Live';
    changeMode(newMode);
  };

  useEffect(() => {
    handleGetAllMethods();
  }, []);

  useEffect(() => {
    dispatch(getUserData());
  }, []);

  useBroadcastChannel('mode-update', handleUpdate);

  if (
    currentPath.includes('/admin/overview') ||
    currentPath.includes('/admin/orders') ||
    currentPath.includes('/admin/users') ||
    currentPath.includes('/admin/batches') ||
    currentPath.includes('/admin/categories') ||
    currentPath.includes('/admin/designer-queries') ||
    currentPath.includes('/admin/pricing-controls/postage') ||
    currentPath.includes('/admin/pricing-controls/cost-of-good') ||
    currentPath.includes('/admin/pricing-controls/pricing') ||
    currentPath.includes('/admin/pricing-controls/plans')
  ) {
    return;
  }

  return (
    <>
      {currentMode && (
        <Box className="modeToggleWrapper">
          <Box
            className={`toggle-switch ${currentMode === 'Live' ? 'live' : 'test'} ${
              tooltipPlacement === 'below'
                ? 'tooltipBelow'
                : tooltipPlacement === 'above'
                  ? 'tooltipAbove'
                  : ''
            }`}
            onClick={() => {
              currentMode === 'Live' ? handleOpenConfirmModal() : handleToggle();
            }}
          >
            {modeChanging ? (
              <Box
                className="modeToggleLoading"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.5,
                  backgroundColor: currentMode === 'Live' ? '#ED5C2F29' : '#F0F0F0',
                }}
              >
                <CircularProgress size={20} sx={{ color: '#ED5C2F' }} />
              </Box>
            ) : (
              <Box className="toggle-track">
                <Box className="toggle-thumb" />
                <Typography
                  className={`toggle-label test-label ${currentMode === 'Test' ? 'active' : ''}`}
                >
                  {MESSAGES.MODE.TEST_LABEL}
                </Typography>
                <Typography
                  className={`toggle-label live-label ${currentMode === 'Live' ? 'active' : ''}`}
                >
                  {MESSAGES.MODE.LIVE_LABEL}
                </Typography>
              </Box>
            )}
            {currentMode === 'Test' && (
              <Box className="modeStatusTooltipBubble" onClick={(event) => event.stopPropagation()}>
                <Typography>{MESSAGES.MODE_REMINDER.TITLE}</Typography>
                <a href={MODE_SWITCHING_URL} rel="noreferrer" target="_blank">
                  {MESSAGES.MODE_REMINDER.LINK_MESSAGE}
                </a>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Stripe>
        <PaymentMethod
          open={paymentModal}
          handleOpen={() => {
            setPaymentModal(true);
          }}
          handleLiveModeSwitch={async (status) => {
            if (paymentMethodMonitor && status) {
              setPaymentMethodMonitor(false);
              const payload = {
                mode: 'live',
              };
              const request = await dispatch(updateMode(payload));
              if (request.status === 200) {
                dispatch(getUserData());
                dispatch({ type: CLEAR_CONTACTS_FILTERS });
              }
            }
          }}
          handleClose={() => {
            setPaymentModal(false);
          }}
        />
      </Stripe>
      {openConfirmModal.open && (
        <ConfirmModal
          openConfirmModal={openConfirmModal}
          setOpenConfirmModal={setOpenConfirmModal}
        />
      )}
    </>
  );
};

export default Mode;
